import { z } from "zod";
import { FidelitySchema, SituationPressureSchema } from "./primitives.js";
import { EnvironmentViewSchema } from "./environment-view.js";
import { ScenePlanSchema } from "./story-event.js";
/**
 * The typed SSE wire contract (apps/api task section 4), shared between
 * apps/api (which produces envelopes) and packages/client (which consumes
 * them) — moved here rather than left apps/api-local because it is
 * genuinely a two-party wire contract, not an implementation detail of
 * either side. `id` always equals the underlying session_events `seq`, so
 * Last-Event-ID resume produces the identical remainder.
 */
export const SseEventTypeSchema = z.enum([
    "BEAT",
    "NARRATION_DELTA",
    "NARRATION_END",
    "DOCUMENT_DELIVERED",
    "NPC_STATE_CHANGED",
    "THREAD_OPENED",
    "PRESSURE_CHANGED",
    "STATUS_CHANGED",
    "ENVIRONMENT_CHANGED",
    "ERROR",
    "HEARTBEAT",
]);
/**
 * A beat advanced. `title` is the slug line of the new in-game day a day crossing reached
 * ("Tuesday, April 7, 8:40 a.m.", contracts' fiction clock), or null for the endgame's beat, which crosses
 * no day: never an authored beat title or an event title (Andre, 2026-10-29). `beatId` stays an id.
 */
export const BeatDataSchema = z.strictObject({ beatId: z.string(), title: z.string().nullable() });
export const NarrationDeltaDataSchema = z.strictObject({ text: z.string() });
export const NarrationEndDataSchema = z.strictObject({ fullText: z.string() });
export const DocumentDeliveredDataSchema = z.strictObject({ documentId: z.string(), kind: z.string(), title: z.string(), fidelity: FidelitySchema });
export const NpcStateChangedDataSchema = z.strictObject({ npcName: z.string(), tell: z.string() });
export const ThreadOpenedDataSchema = z.strictObject({ summary: z.string() });
export const PressureChangedDataSchema = z.strictObject({ pressure: SituationPressureSchema });
export const StatusChangedDataSchema = z.strictObject({ status: z.string() });
/**
 * ADR 0009 commitment 1: the COMPLETE new EnvironmentView, shipped whole
 * (never a diff) so a resumed or reconnected client is always one envelope
 * away from a correct scene. Emitted at most once per turn, after the
 * turn's other envelopes.
 */
/**
 * The complete new view (ADR 0009: whole, never a diff) plus the turn's
 * transient ScenePlan (ADR 0014): the physical steps the client plays
 * between the previous view and this one, then forgets. Null when nothing
 * on the stage moved. The state fetch carries the view alone — a resumed
 * session draws the result, it does not replay the walk.
 */
export const EnvironmentChangedDataSchema = z.strictObject({ view: EnvironmentViewSchema, plan: ScenePlanSchema.nullable() });
export const ErrorDataSchema = z.strictObject({ code: z.string(), message: z.string() });
export const HeartbeatDataSchema = z.strictObject({});
//# sourceMappingURL=sse.js.map